import { useEffect } from 'react';

import { ActionIcon, Box, Card, Group, Text, ThemeIcon } from 'ui';
import { IconDelete, IconDuplicate, IconGripVertical } from 'ui/Icons';

import classes from './DroppedElementCardContent.module.css';
import type { BottomSectionValues, DroppedElement } from './types';

// Component that contains the card content and can consume the focused prop
export const DroppedElementCardContent = ({
  element,
  dragHandleRef,
  handleDuplicate,
  handleDelete,
  onFocusChange,
  onValueChange,
  focused,
  onBottomSectionValueChange,
  renderComponent,
}: {
  element: DroppedElement;
  dragHandleRef: React.RefObject<HTMLDivElement>;
  handleDuplicate: () => void;
  handleDelete: () => void;
  onFocusChange?: (focused: boolean) => void;
  onValueChange?: (elementId: string, value: string) => void;
  focused?: boolean;
  onBottomSectionValueChange?: (elementId: string, values: BottomSectionValues) => void;
  renderComponent: (
    element: DroppedElement,
    focused: boolean,
    onValueChange?: (elementId: string, value: string) => void,
    onBottomSectionValueChange?: (elementId: string, values: BottomSectionValues) => void
  ) => React.ReactNode;
}) => {
  // Notify parent of focus changes
  useEffect(() => {
    onFocusChange?.(!!focused);
  }, [focused, onFocusChange]);

  return (
    <>
      {/* Conditionally show header only when focused */}
      {focused && (
        <Card.Section p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-1)' }}>
          <Group justify="space-between" align="center">
            <Group gap="xs" align="center">
              <Box ref={dragHandleRef} className={classes.dragHandle}>
                <ThemeIcon variant="transparent" color="gray.4">
                  <IconGripVertical size={24} brand="tabler" />
                </ThemeIcon>
              </Box>
              <Text size="md" fw={600} c="gray.8">
                {element.label}
              </Text>
            </Group>
            <Group gap="xs">
              <ActionIcon
                size="sm"
                variant="light"
                color="gray.6"
                onClick={handleDuplicate}
                aria-label="Duplicate element"
              >
                <IconDuplicate size={14} />
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="filled"
                color="cerise.7"
                onClick={handleDelete}
                aria-label="Delete element"
              >
                <IconDelete size={14} />
              </ActionIcon>
            </Group>
          </Group>
        </Card.Section>
      )}
      <Card.Section p="md">
        {renderComponent(element, !!focused, onValueChange, onBottomSectionValueChange)}
      </Card.Section>
    </>
  );
};
